import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import ProgressBar from "../ProgressBar";
import { formatInteger } from "../../utils/formatters";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface ProgramDetail {
  id: number;
  dia: number;
  gauge: string;
  program_type: string;
  weight_in_kilo: number;
  number_of_rolls?: number;
  texture?: number;
  design?: string;
  completed_weight: number;
  progress_percentage: number;
  remaining_weight: number;
  completed?: boolean;
  status?: string;
}

interface EmployeeShiftDetail {
  id: number;
  program_id: number;
  dia: string;
  gauge: string;
  machine_type: string;
  rolls: number;
  weight: number;
}

interface Program {
  id: number;
  company: {
    id: number;
    name: string;
  };
  program_details: ProgramDetail[];
  lot?: string;
  status: "not_started" | "in_progress" | "completed" | "delivered";
  created_at: string;
  total_weight: number;
  specs_display: string;
  types_display: string;
  designs_display: string;
  employee_shift_details?: EmployeeShiftDetail[];
}

export default function PublicProgramView() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [program, setProgram] = useState<Program | null>(null);

  useEffect(() => {
    fetchProgram();
  }, [shareToken]);

  const fetchProgram = async () => {
    try {
      setLoading(true);
      // Use short URL endpoint if accessed via /p/ route (for view tracking)
      const isShortUrl = location.pathname.startsWith('/p/');
      const apiEndpoint = isShortUrl 
        ? `${API_URL}/p/${shareToken}` 
        : `${API_URL}/public/programs/${shareToken}`;
      
      // Include auth token if available - backend uses this to exclude internal views from analytics
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = token;
      }
      
      const response = await fetch(apiEndpoint, { headers });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Program not found or link is invalid");
        }
        throw new Error("Failed to load program details");
      }

      const data = await response.json();
      setProgram(data);
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to load program details");
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (detail: ProgramDetail) => {
    if (!program?.employee_shift_details) {
      return {
        completedWeight: 0,
        weightPercentage: 0,
        completedRolls: 0,
        rollsPercentage: 0,
        hasRollsConstraint: !!detail.number_of_rolls,
      };
    }

    const matchingShiftDetails = program.employee_shift_details.filter(
      (sd) =>
        sd.dia === detail.dia.toString() &&
        sd.gauge === detail.gauge &&
        sd.machine_type === detail.program_type
    );

    const completedWeight = matchingShiftDetails.reduce(
      (sum, sd) => sum + Number(sd.weight),
      0
    );
    const completedRolls = matchingShiftDetails.reduce(
      (sum, sd) => sum + Number(sd.rolls),
      0
    );

    const totalWeight = detail.weight_in_kilo;
    const totalRolls = detail.number_of_rolls || 0;

    const weightPercentage =
      totalWeight > 0 ? (completedWeight / totalWeight) * 100 : 0;
    const rollsPercentage =
      totalRolls > 0 ? (completedRolls / totalRolls) * 100 : 0;

    return {
      completedWeight,
      weightPercentage: Math.min(weightPercentage, 100),
      completedRolls,
      rollsPercentage: Math.min(rollsPercentage, 100),
      hasRollsConstraint: !!detail.number_of_rolls,
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "not_started":
        return "default";
      case "in_progress":
        return "primary";
      case "completed":
        return "success";
      case "delivered":
        return "info";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: "#f5f5f5" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: "#f5f5f5", p: 2 }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!program) {
    return null;
  }

  return (
    <Box sx={{ backgroundColor: "#f5f5f5", minHeight: "100vh", py: { xs: 2, sm: 4 }, pt: { xs: 3, sm: 5 } }}>
      <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Box sx={{ textAlign: "center", mb: 3, pt: 2 }}>
              <Box
                component="img"
                src="/icon-192.png"
                alt="Devi Impex Logo"
                sx={{
                  width: 80,
                  height: 80,
                  mb: 2,
                  borderRadius: 2,
                }}
              />
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                Devi Impex
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Program Status
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Program Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Program Details
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Program ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    #{program.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {program.company.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Lot
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {program.lot || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={getStatusLabel(program.status)}
                    color={getStatusColor(program.status)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Program Details Table - Desktop View */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Production Details
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell>
                        <strong>Dia</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Gauge</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Type</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Weight (kg)</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Rolls</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Texture</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Progress</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Design</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {program?.program_details.map((detail) => {
                      const progress = calculateProgress(detail);
                      return (
                        <TableRow key={detail.id}>
                          <TableCell>
                            <DiaCircle
                              dia={detail.dia}
                              gauge={detail.gauge}
                              machineType={detail.program_type}
                              size="small"
                              disableLink={true}
                            />
                          </TableCell>
                          <TableCell>
                            <GaugeChip gauge={detail.gauge} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={detail.program_type}
                              size="small"
                              color={
                                detail.program_type === "rib"
                                  ? "primary"
                                  : "secondary"
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            {detail.weight_in_kilo} kg
                          </TableCell>
                          <TableCell align="right">
                            {detail.number_of_rolls ? formatInteger(detail.number_of_rolls) : "-"}
                          </TableCell>
                          <TableCell>{detail.texture || "-"}</TableCell>
                          <TableCell>
                            <Box sx={{ minWidth: 160 }}>
                              <ProgressBar
                                type="weight"
                                done={progress.completedWeight}
                                total={detail.weight_in_kilo}
                                size="small"
                              />
                              {progress.hasRollsConstraint && (
                                <Box sx={{ mt: 1 }}>
                                  <ProgressBar
                                    type="rolls"
                                    done={progress.completedRolls}
                                    total={detail.number_of_rolls!}
                                    remainingWeight={detail.weight_in_kilo - progress.completedWeight}
                                    size="small"
                                    hideKgPerRoll
                                  />
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{detail.design || "-"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Program Details Cards - Mobile View */}
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Production Details
              </Typography>
              {program?.program_details.map((detail, index) => {
                const progress = calculateProgress(detail);
                return (
                  <Card 
                    key={detail.id} 
                    variant="outlined" 
                    sx={{ 
                      mb: 2,
                      borderLeft: 4,
                      borderLeftColor: detail.program_type === "rib" ? "primary.main" : "secondary.main"
                    }}
                  >
                    <CardContent>
                      {/* Header with Dia, Gauge, Type */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                        <DiaCircle
                          dia={detail.dia}
                          gauge={detail.gauge}
                          machineType={detail.program_type}
                          size="small"
                          disableLink={true}
                        />
                        <GaugeChip gauge={detail.gauge} />
                        <Chip
                          label={detail.program_type}
                          size="small"
                          color={
                            detail.program_type === "rib"
                              ? "primary"
                              : "secondary"
                          }
                        />
                      </Box>

                      {/* Details Grid */}
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Weight
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {detail.weight_in_kilo} kg
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Rolls
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {detail.number_of_rolls ? formatInteger(detail.number_of_rolls) : "-"}
                          </Typography>
                        </Box>
                        {detail.texture && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Texture
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {detail.texture}
                            </Typography>
                          </Box>
                        )}
                        {detail.design && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Design
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {detail.design}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Progress Section */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Progress
                        </Typography>
                        
                        <ProgressBar
                          type="weight"
                          done={progress.completedWeight}
                          total={detail.weight_in_kilo}
                          size="small"
                        />
                        
                        {progress.hasRollsConstraint && (
                          <Box sx={{ mt: 1 }}>
                            <ProgressBar
                              type="rolls"
                              done={progress.completedRolls}
                              total={detail.number_of_rolls!}
                              remainingWeight={detail.weight_in_kilo - progress.completedWeight}
                              size="small"
                              hideKgPerRoll
                            />
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Thank you for your business!
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
